<template>
    <tailwind-layout title="Billing">
        <template #header></template>

        <div class="mx-auto space-y-6">
            <div>
                <p class="text-sm text-gray-500">Settings / Billing</p>
                <h2 class="font-semibold text-2xl leading-tight">Billing &amp; payments</h2>
            </div>

            <!-- Legacy landlords: old subscriptions table + switch-to-new-plan banner -->
            <template v-if="planState === 'legacy'">
                <div
                    v-if="isOnEarlierPlan"
                    id="switch-plan"
                    class="flex flex-col gap-3 rounded-lg border border-[#e0cb85] bg-[#fbf6e3] p-4 sm:flex-row sm:items-center sm:justify-between"
                    data-test="switch-plan-banner"
                >
                    <p class="text-sm text-gray-800">
                        You're on an earlier RentRisk plan. Switch to RiskGuard Pro: one yearly plan for every property.
                    </p>
                    <button
                        type="button"
                        class="shrink-0 rounded-md bg-gold-500 px-4 py-2 text-sm font-bold text-[#7d6310] hover:bg-gold-700"
                        @click="showSwitchPlanModal = true"
                    >
                        Switch to new plan
                    </button>
                </div>

                <legacy-subscriptions-card
                    :rent-payment-subscription="rent_payment_subscription"
                    :maintenance-subscription="maintenance_subscription"
                    :rent-payment-feature="rent_payment_feature"
                    :maintenance-feature="maintenance_feature"
                    :protect-subscription="protect_subscription"
                    :protect-annual-price="protect_annual_price"
                    :protect-pro-annual-price="protect_pro_annual_price"
                    :is-submitting="isSubmitting"
                    @cancel="showCancelConfirmation"
                    @resume-rent-payment="resumeRentPaymentSubscription"
                    @resume-maintenance="resumeMaintenanceSubscription"
                    @resume-protect="resumeProtectSubscription"
                    @cancel-property="showPropertyCancelConfirmation"
                    @cancel-pending-downgrade="cancelPendingDowngrade"
                />
            </template>

            <!-- Account-wide Pro / Basic -->
            <template v-else-if="planState === 'pro' || planState === 'basic'">
                <div class="grid gap-6 lg:grid-cols-3 lg:items-stretch">
                    <current-plan-card
                        class="lg:col-span-2"
                        :pro-plan="proPlan"
                        @change-plan="handleChangePlan"
                        @manage-payment="scrollToPayment"
                    />
                    <properties-usage-card class="lg:col-span-1" :pro-plan="proPlan" @upgrade="startProUpgrade" />
                </div>

                <div class="grid gap-6 lg:grid-cols-3 lg:items-stretch">
                    <plans-pricing-card class="lg:col-span-2" :pro-tiers="proTiers" :pro-plan="proPlan" />
                    <payment-method-card
                        class="lg:col-span-1"
                        :payment-method="user.payment_method"
                        :billing-email="billing_email"
                        @add-card="proceedWithAddCard"
                        @delete-card="deletePaymentMethod"
                    />
                </div>
            </template>

            <!-- Payment method, full width for legacy and the pro_plan-unavailable fallback -->
            <payment-method-card
                v-if="planState !== 'pro' && planState !== 'basic'"
                :payment-method="user.payment_method"
                :billing-email="billing_email"
                @add-card="proceedWithAddCard"
                @delete-card="deletePaymentMethod"
            />

            <invoice-history-card :invoices="invoices" />

            <getting-paid-section
                :affiliate-account="affiliate_account"
                :bank-accounts="bank_accounts"
                :is-kyc="is_kyc"
                :transfer-diligence-status="transfer_diligence_status"
                :is-onboarding-info-submitted="is_onboarding_info_submitted"
                :needs-plaid-owner-verification="needs_plaid_owner_verification"
                :originator="originator"
                :steps="steps"
                :plaid-resubmission-enabled="plaid_resubmission_enabled"
                :needs-phone-number="needs_phone_number"
                :has-rent-payments-access="hasRentPaymentsAccess"
                :has-rent-payments-subscription="hasRentPaymentsSubscription"
                :pro-cta-state="proCtaState"
                :is-on-earlier-plan="isOnEarlierPlan"
                @create-link-token="createLinkToken"
                @onboarding-step1="onboardingStep1"
                @show-missing-req="isShowMissingReqModal = true"
                @show-plaid-resubmission="isShowPlaidResubmissionModal = true"
                @show-phone="showPhoneModal = true"
                @start-pro-upgrade="startProUpgrade"
            />

            <cancel-pro-card
                v-if="planState === 'pro'"
                :pro-plan="proPlan"
                :is-submitting="isSubmitting"
                @cancel="showCancelConfirmation('protect-pro')"
                @resume="resumeProtectSubscription"
            />
        </div>

        <!-- Delete Card Modal -->
        <div v-if="showDeleteModal">
            <TransitionRoot as="template" :show="showDeleteModal">
                <Dialog as="div" class="relative z-50" @close="showDeleteModal = false">
                    <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0" enter-to="opacity-100" leave="ease-in duration-200" leave-from="opacity-100" leave-to="opacity-0">
                        <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                    </TransitionChild>
                    <div class="fixed inset-0 z-10 w-screen overflow-y-auto">
                        <div class="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                            <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95" enter-to="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-200" leave-from="opacity-100 translate-y-0 sm:scale-100" leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
                                <DialogPanel class="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-sm sm:p-6">
                                    <div>
                                        <div class="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
                                            <font-awesome-icon :icon="faBoxArchive" class="h-6 w-6 text-red-500" aria-hidden="true" />
                                        </div>
                                        <div class="mt-3 text-center sm:mt-5">
                                            <DialogTitle as="h3" class="text-base font-semibold leading-6 text-gray-900">Are you sure you want to delete this Card?</DialogTitle>
                                            <div class="mt-2">
                                                If you have any active subscriptions, they will fail to renew unless you add a new payment method first.
                                            </div>
                                        </div>
                                    </div>
                                    <div class="mt-5 sm:mt-6 flex">
                                        <button type="button" class="flex-shrink-0 w-1/2 mr-1 justify-center rounded-md bg-gray-50 border border-gray-200 px-3 py-2 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-200" @click="showDeleteModal = false">Cancel</button>
                                        <button type="button" class="primaryButton w-1/2 ml-1 justify-center rounded-md px-3 py-2 text-sm font-semibold text-white hover:bg-black" @click="destroy()">Confirm</button>
                                    </div>
                                </DialogPanel>
                            </TransitionChild>
                        </div>
                    </div>
                </Dialog>
            </TransitionRoot>
        </div>

        <!-- Add Credit Card Modal -->
        <add-card-popup :show="showAddCardModal" :stripe_data="stripe_data" :settle-open-invoices="true" :replace-existing="userHasCard" @card-added="handleCardAdded" @close="closeCardModal()"></add-card-popup>

        <!-- Switch to new plan (legacy → account-wide Pro) -->
        <SwitchToNewPlanModal
            :show="showSwitchPlanModal"
            :stripe-data="stripe_data?.publishable_key ? stripe_data : null"
            @close="closeSwitchPlanModal"
            @switched="closeSwitchPlanModal"
        />

        <!-- 86bc9e403: buying Pro from Billing opens the combined in-place checkout instead
             of bouncing to /pricing. The pop-up subscribes itself via the ref below. -->
        <RiskGuardProUpsellModal
            v-if="canBuyProInPlace"
            :show="showProUpgradeModal"
            :processing="isProUpgrading"
            :stripe-data="stripe_data?.publishable_key ? stripe_data : null"
            :user-payment-method="user.payment_method"
            :subscription="subscriptionProtectionInstance"
            :show-features="true"
            :notice="proTierChangedNotice"
            @subscribe="onProSubscribeStart"
            @dismiss="closeProUpgradeModal"
        />

        <SubscriptionProtection
            v-if="canBuyProInPlace"
            ref="subscriptionProtection"
            subscription-type="protect"
            plan-slug="protect-pro-tiered"
            billing-cycle="annual"
            :terms-accepted="Boolean(user?.protect_tos_accepted_at)"
            :has-payment-method="userHasCard"
            :user-payment-method="user.payment_method"
            :stripe-data="stripe_data ?? []"
            @success="onProSubscribeSuccess"
            @error="onProSubscribeError"
            @cancel="isProUpgrading = false"
            @tier-changed="onProTierChanged"
        />

        <!-- Cancel Confirmation Modal -->
        <confirmation-modal
            :show="showCancelModal"
            :title="cancelTitle"
            :message="cancelMessage"
            :confirm-button-text="cancelConfirmText"
            cancel-button-text="No, Keep Subscription"
            confirm-button-class="dangerButton w-1/2 ml-1 justify-center rounded-md px-3 py-2 text-sm font-semibold text-white hover:bg-red-700"
            :is-loading="isSubmitting"
            @close="closeCancelModal"
            @confirm="confirmCancel"
        ></confirmation-modal>

        <!-- Onboarding Step 1 Modal -->
        <div v-if="isShowStep1Modal">
            <TransitionRoot as="template" :show="isShowStep1Modal">
                <Dialog as="div" class="relative z-50" @close="isShowStep1Modal = false">
                    <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0" enter-to="opacity-100" leave="ease-in duration-200" leave-from="opacity-100" leave-to="opacity-0">
                        <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                    </TransitionChild>
                    <div class="fixed inset-0 z-10 w-screen overflow-y-auto">
                        <div class="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                            <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95" enter-to="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-200" leave-from="opacity-100 translate-y-0 sm:scale-100" leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
                                <DialogPanel class="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-md sm:p-6">
                                    <div>
                                        <div class="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-purple-100">
                                            <font-awesome-icon :icon="faBuilding" class="h-6 w-6 text-purple-600" aria-hidden="true" />
                                        </div>
                                        <div class="mt-3 text-center sm:mt-5">
                                            <DialogTitle as="h3" class="text-base font-semibold leading-6 text-gray-900">Landlord Onboarding</DialogTitle>
                                            <div class="mt-2"><p class="text-sm text-gray-500">What type of business are you?</p></div>
                                        </div>
                                    </div>
                                    <div class="mt-6">
                                        <div class="space-y-4">
                                            <label class="flex items-center p-4 border rounded-lg cursor-pointer hover:border-purple-500 transition"
                                                   :class="{ 'border-purple-600 ring-2 ring-purple-200': business_type === 'individual' }">
                                                <input type="radio" name="business_type" value="individual" v-model="business_type" class="form-radio text-purple-600 hidden" />
                                                <div class="flex flex-col ml-2">
                                                    <span class="text-base font-semibold text-gray-800">Individual</span>
                                                    <span class="text-sm text-gray-500">Personal landlord</span>
                                                </div>
                                            </label>
                                            <label class="flex items-center p-4 border rounded-lg cursor-pointer hover:border-purple-500 transition"
                                                   :class="{ 'border-purple-600 ring-2 ring-purple-200': business_type === 'business' }">
                                                <input type="radio" name="business_type" value="business" v-model="business_type" class="form-radio text-purple-600 hidden" />
                                                <div class="flex flex-col ml-2">
                                                    <span class="text-base font-semibold text-gray-800">Business</span>
                                                    <span class="text-sm text-gray-500">Company or organization</span>
                                                </div>
                                            </label>
                                        </div>
                                    </div>
                                    <div class="mt-5 sm:mt-6 flex gap-2">
                                        <button type="button" class="whiteButton w-1/2" @click="isShowStep1Modal = false">Cancel</button>
                                        <button @click="proceedToOnboarding" class="primaryButton w-1/2">Continue</button>
                                    </div>
                                </DialogPanel>
                            </TransitionChild>
                        </div>
                    </div>
                </Dialog>
            </TransitionRoot>
        </div>

        <!-- Phone Number Modal -->
        <div v-if="showPhoneModal">
            <TransitionRoot as="template" :show="showPhoneModal">
                <Dialog as="div" class="relative z-50" @close="closePhoneModal">
                    <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0" enter-to="opacity-100" leave="ease-in duration-200" leave-from="opacity-100" leave-to="opacity-0">
                        <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                    </TransitionChild>
                    <div class="fixed inset-0 z-10 w-screen overflow-y-auto">
                        <div class="flex min-h-full items-center justify-center p-4 text-center sm:items-center sm:p-0">
                            <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95" enter-to="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-200" leave-from="opacity-100 translate-y-0 sm:scale-100" leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
                                <DialogPanel class="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-md sm:p-6">
                                    <div>
                                        <div class="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-blue-100">
                                            <font-awesome-icon :icon="faPhone" class="h-6 w-6 text-blue-600" aria-hidden="true" />
                                        </div>
                                        <div class="mt-3 text-center sm:mt-5">
                                            <DialogTitle as="h3" class="text-base font-semibold leading-6 text-gray-900">Phone Number Required</DialogTitle>
                                            <div class="mt-2">
                                                <p class="text-sm text-gray-500">To complete the setup for rent payments, we need to verify your phone number. This helps ensure secure transactions and account verification.</p>
                                            </div>
                                        </div>
                                    </div>
                                    <div class="mt-6">
                                        <div class="space-y-4">
                                            <div>
                                                <label for="phone" class="block text-sm font-medium text-gray-700">Phone Number <span class="text-red-500">*</span></label>
                                                <div class="mt-1">
                                                    <input id="phone" v-model="phoneForm.phone" type="tel" maxlength="10" placeholder="1234567890"
                                                        class="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md"
                                                        :class="phoneForm.errors.phone ? 'border-red-300' : ''" />
                                                    <p v-if="phoneForm.errors.phone" class="mt-1 text-sm text-red-500">{{ phoneForm.errors.phone }}</p>
                                                    <p class="mt-1 text-xs text-gray-500">Enter 10 digits without spaces or dashes</p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div class="mt-5 sm:mt-6 flex gap-2">
                                        <button type="button" class="whiteButton w-1/2" @click="closePhoneModal" :disabled="isSubmitting">Cancel</button>
                                        <button @click="savePhoneNumber" :disabled="isSubmitting || !phoneForm.phone || phoneForm.phone.length !== 10" class="primaryButton w-1/2 disabled:opacity-50 disabled:cursor-not-allowed">
                                            {{ isSubmitting ? 'Saving...' : 'Save Phone' }}
                                        </button>
                                    </div>
                                </DialogPanel>
                            </TransitionChild>
                        </div>
                    </div>
                </Dialog>
            </TransitionRoot>
        </div>

        <!-- Missing Requirements Modal -->
        <missing-requirements-modal
            :show="isShowMissingReqModal"
            :originator="originator"
            :user_business_type="user_business_type"
            :states="states"
            @close="isShowMissingReqModal = false"
        />

        <plaid-resubmission-modal
            :show="isShowPlaidResubmissionModal"
            :fields="plaid_resubmission_fields"
            :states="states"
            @close="isShowPlaidResubmissionModal = false"
        />

        <full-page-loader v-if="isLoader"></full-page-loader>
    </tailwind-layout>
</template>

<script>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faBoxArchive, faBuilding, faPhone } from '@fortawesome/pro-light-svg-icons';

import TailwindLayout from '@/Layouts/TailwindLayout.vue';
import { Dialog, DialogPanel, DialogTitle, TransitionChild, TransitionRoot } from '@headlessui/vue';
import AddCardPopup from '@/Shared/AddCardPopup.vue';
import ConfirmationModal from '@/Shared/ConfirmationModal.vue';
import axios from 'axios';
import FullPageLoader from '@/Shared/FullPageLoader.vue';
import MissingRequirementsModal from '@/Shared/MissingRequirementsModal.vue';
import PlaidResubmissionModal from '@/Shared/PlaidResubmissionModal.vue';
import SwitchToNewPlanModal from '@/Components/SwitchToNewPlanModal.vue';
import RiskGuardProUpsellModal from '@/Components/RiskGuardProUpsellModal.vue';
import SubscriptionProtection from '@/Shared/V2/SubscriptionProtection.vue';
import { proCta } from '@/Shared/proPlanState';

import CurrentPlanCard from '@/Pages/PaymentMethods/Partials/CurrentPlanCard.vue';
import PropertiesUsageCard from '@/Pages/PaymentMethods/Partials/PropertiesUsageCard.vue';
import PlansPricingCard from '@/Pages/PaymentMethods/Partials/PlansPricingCard.vue';
import PaymentMethodCard from '@/Pages/PaymentMethods/Partials/PaymentMethodCard.vue';
import InvoiceHistoryCard from '@/Pages/PaymentMethods/Partials/InvoiceHistoryCard.vue';
import GettingPaidSection from '@/Pages/PaymentMethods/Partials/GettingPaidSection.vue';
import CancelProCard from '@/Pages/PaymentMethods/Partials/CancelProCard.vue';
import LegacySubscriptionsCard from '@/Pages/PaymentMethods/Partials/LegacySubscriptionsCard.vue';

export default {
    components: {
        Dialog,
        DialogTitle,
        DialogPanel,
        TransitionChild,
        TransitionRoot,
        TailwindLayout,
        AddCardPopup,
        ConfirmationModal,
        FullPageLoader,
        MissingRequirementsModal,
        PlaidResubmissionModal,
        SwitchToNewPlanModal,
        RiskGuardProUpsellModal,
        SubscriptionProtection,
        FontAwesomeIcon,
        CurrentPlanCard,
        PropertiesUsageCard,
        PlansPricingCard,
        PaymentMethodCard,
        InvoiceHistoryCard,
        GettingPaidSection,
        CancelProCard,
        LegacySubscriptionsCard,
    },
    props: {
        user: Object,
        affiliate_account: Object,
        rent_payment_subscription: Object,
        maintenance_subscription: Object,
        rent_payment_feature: Object,
        maintenance_feature: Object,
        protect_subscription: Object,
        subscription_price: String,
        protect_pro_subscription_price: String,
        protect_annual_price: Number,
        protect_annual_savings_percentage: Number,
        protect_pro_annual_price: Number,
        protect_pro_annual_savings_percentage: Number,
        monthly_fee: String,
        landlord_monthly_recurring_fee: String,
        stripe_data: Object,
        terms_accepted: Object,
        bank_accounts: Array,
        is_kyc: Boolean,
        transfer_diligence_status: String,
        is_onboarding_info_submitted: Boolean,
        needs_plaid_owner_verification: Boolean,
        originator: Object,
        steps: Array,
        steps_status: Object,
        user_business_type: String,
        states: Array,
        needs_phone_number: Boolean,
        plaid_resubmission_enabled: Boolean,
        plaid_resubmission_fields: Object,
        // 86bcd9hgt (Part 3)
        billing_email: String,
        invoices: {
            type: Array,
            default: () => [],
        },
    },
    data() {
        return {
            faBoxArchive,
            faBuilding,
            faPhone,
            showSwitchPlanModal: false,
            showDeleteModal: false,
            selected_payment_method_id: null,
            isSubmitting: false,
            showAddCardModal: false,
            showCancelModal: false,
            cancelAction: null,
            cancelTitle: '',
            cancelMessage: '',
            cancelConfirmText: 'Yes, Cancel Subscription',
            cancelPropertyId: null,
            isLoader: false,
            isShowStep1Modal: false,
            business_type: 'individual',
            showPhoneModal: false,
            phoneForm: {
                phone: '',
                errors: { phone: null },
            },
            isShowMissingReqModal: false,
            isShowPlaidResubmissionModal: false,
            // 86bc9e403: in-place Pro checkout from Billing.
            showProUpgradeModal: false,
            isProUpgrading: false,
            proTierChangedNotice: null,
            subscriptionProtectionInstance: null,
        };
    },
    computed: {
        userHasCard() {
            return !! this.user?.payment_method;
        },
        proPlan() {
            return this.$page.props.auth?.user?.pro_plan ?? null;
        },
        planState() {
            return this.proPlan?.state ?? null;
        },
        proTiers() {
            return this.$page.props.subscription_prices?.pro_tiers ?? [];
        },
        proCtaState() {
            return proCta(this.proPlan);
        },
        isOnEarlierPlan() {
            return this.proCtaState === 'switch';
        },
        // Basic landlords buy Pro in place here; an earlier plan uses "Switch to new plan",
        // Pro / failed payment don't buy from this pop-up.
        canBuyProInPlace() {
            return this.proCtaState === 'upgrade';
        },
        hasActiveProtectSubscription() {
            return this.protect_subscription &&
                (['active', 'trialing'].includes(this.protect_subscription.stripe_sub_status) ||
                    this.protect_subscription.is_cancellation_requested);
        },
        hasRentPaymentsAccess() {
            const hasSubscription = (this.rent_payment_subscription &&
                (['active', 'trialing'].includes(this.rent_payment_subscription.stripe_sub_status) ||
                    this.rent_payment_subscription.is_cancellation_requested)) ||
                this.hasActiveProtectSubscription;

            return hasSubscription && !this.needs_phone_number;
        },
        hasRentPaymentsSubscription() {
            return (this.rent_payment_subscription &&
                (['active', 'trialing'].includes(this.rent_payment_subscription.stripe_sub_status) ||
                    this.rent_payment_subscription.is_cancellation_requested)) ||
                this.hasActiveProtectSubscription;
        },
    },
    mounted() {
        this.openSwitchFromHash();
        window.addEventListener('hashchange', this.openSwitchFromHash);
        // The combined Pro checkout pop-up subscribes through this instance.
        this.$nextTick(() => {
            this.subscriptionProtectionInstance = this.$refs.subscriptionProtection ?? null;
        });
    },
    beforeUnmount() {
        window.removeEventListener('hashchange', this.openSwitchFromHash);
    },
    methods: {
        // Change plan from the Current plan card: Basic → upsell, legacy → switch,
        // Pro with a payment problem → update card, healthy Pro → compare on pricing.
        handleChangePlan() {
            this.startProUpgrade();
        },
        scrollToPayment() {
            const el = document.getElementById('payment-method-card');
            if (el) {
                el.scrollIntoView({ behavior: 'smooth' });
            }
        },
        deletePaymentMethod(paymentMethodID) {
            this.selected_payment_method_id = paymentMethodID;
            this.showDeleteModal = true;
        },
        destroy() {
            this.$inertia.delete(this.route('payment-methods.destroy', this.selected_payment_method_id), {
                onFinish: () => {
                    this.showDeleteModal = false;
                },
            });
        },
        async cancelRentPaymentSubscription() {
            if (this.isSubmitting) return;
            this.isSubmitting = true;
            try {
                await this.$inertia.post(route('rent-payments.monthly-subscription.cancel'), {}, {
                    onFinish: () => { this.isSubmitting = false; },
                });
            } catch (error) {
                this.isSubmitting = false;
            }
        },
        async resumeRentPaymentSubscription() {
            if (this.isSubmitting) return;
            this.isSubmitting = true;
            try {
                await this.$inertia.post(route('rent-payments.subscription.resume'), {}, {
                    onFinish: () => { this.isSubmitting = false; },
                });
            } catch (error) {
                this.isSubmitting = false;
            }
        },
        async cancelMaintenanceSubscription() {
            if (this.isSubmitting) return;
            this.isSubmitting = true;
            try {
                await this.$inertia.post(route('maintenance.monthly-subscription.cancel'), {}, {
                    onFinish: () => { this.isSubmitting = false; },
                });
            } catch (error) {
                this.isSubmitting = false;
            }
        },
        async resumeMaintenanceSubscription() {
            if (this.isSubmitting) return;
            this.isSubmitting = true;
            try {
                await this.$inertia.post(route('maintenance.subscription.resume'), {}, {
                    onFinish: () => { this.isSubmitting = false; },
                });
            } catch (error) {
                this.isSubmitting = false;
            }
        },
        // RAC-10387: per-property cancel — property id captured before the modal reset clears it
        async cancelPropertyFeatureSubscription(cancelRoute) {
            const propertyId = this.cancelPropertyId;
            if (this.isSubmitting || !propertyId) return;
            this.isSubmitting = true;
            try {
                await this.$inertia.post(cancelRoute, { property_id: propertyId }, {
                    onFinish: () => { this.isSubmitting = false; },
                });
            } catch (error) {
                this.isSubmitting = false;
            }
        },
        // The top bar links to #switch-plan; on this page that is only a hash change, so
        // listen for it too. The hash is cleared on close so the pill works again.
        openSwitchFromHash() {
            if (window.location.hash === '#switch-plan' && this.isOnEarlierPlan) {
                this.showSwitchPlanModal = true;
            }
        },
        closeSwitchPlanModal() {
            this.showSwitchPlanModal = false;
            if (window.location.hash === '#switch-plan') {
                history.replaceState(history.state, '', window.location.pathname + window.location.search);
            }
        },
        startProUpgrade() {
            if (this.proCtaState === 'switch') {
                this.showSwitchPlanModal = true;
                return;
            }
            if (this.proCtaState === 'fix-card') {
                this.showAddCardModal = true;
                return;
            }
            // 86bc9e403: open the combined Pro checkout in place instead of bouncing to /pricing.
            this.proTierChangedNotice = null;
            this.showProUpgradeModal = true;
        },
        closeProUpgradeModal() {
            this.showProUpgradeModal = false;
            this.proTierChangedNotice = null;
            this.isProUpgrading = false;
        },
        onProSubscribeStart() {
            this.isProUpgrading = true;
        },
        onProSubscribeSuccess() {
            // SubscriptionProtection already reloaded the page in place; just close the pop-up.
            this.showProUpgradeModal = false;
            this.isProUpgrading = false;
        },
        onProSubscribeError({ error } = {}) {
            // 86bc9e403 #1: show the reason inside the still-open pop-up (its notice) so the
            // re-enabled Subscribe button sits next to the failure message, not a toast behind it.
            this.isProUpgrading = false;
            this.proTierChangedNotice = error || 'We could not start your subscription. Please try again.';
        },
        // The price moved since the pop-up (a property was added meanwhile); nothing was charged.
        onProTierChanged({ message } = {}) {
            this.isProUpgrading = false;
            this.$inertia.reload({
                only: ['auth'],
                onSuccess: () => {
                    this.proTierChangedNotice = message || null;
                    this.showProUpgradeModal = true;
                },
            });
        },
        async cancelProtectSubscription() {
            if (this.isSubmitting) return;
            this.isSubmitting = true;
            try {
                await this.$inertia.post(route('protect-subscription.cancel'), {}, {
                    onFinish: () => { this.isSubmitting = false; },
                });
            } catch (error) {
                this.isSubmitting = false;
            }
        },
        async resumeProtectSubscription() {
            if (this.isSubmitting) return;
            this.isSubmitting = true;
            try {
                await this.$inertia.post(route('protect-subscription.resume'), {}, {
                    onFinish: () => { this.isSubmitting = false; },
                });
            } catch (error) {
                this.isSubmitting = false;
            }
        },
        async cancelPendingDowngrade() {
            if (this.isSubmitting) return;
            this.isSubmitting = true;
            try {
                await this.$inertia.post(route('protect-subscription.cancel-pending-downgrade'), {}, {
                    onFinish: () => { this.isSubmitting = false; },
                });
            } catch (error) {
                this.isSubmitting = false;
            }
        },
        handleCardAdded({ payment_method, success, message }) {
            this.showAddCardModal = false;
            if (!success) {
                if (this.$page.props.flash) {
                    this.$page.props.flash.error = message || 'We could not save your card. Please try again.';
                }
                return;
            }

            this.user.payment_method = payment_method;

            // Reload to pull the updated card, pro_plan state and deferred invoices, then
            // surface the step-6 "Card saved. Paid N invoice(s)…" message as the flash toast.
            this.$inertia.reload({
                onSuccess: () => {
                    if (this.$page.props.flash) {
                        this.$page.props.flash.success = message || 'Card added successfully.';
                    }
                },
            });
        },
        closeCardModal() {
            this.showAddCardModal = false;
        },
        proceedWithAddCard() {
            this.showAddCardModal = true;
        },
        showCancelConfirmation(subscriptionType) {
            this.cancelAction = subscriptionType;
            this.cancelConfirmText = 'Yes, Cancel Subscription';

            switch (subscriptionType) {
                case 'rent_payment':
                    this.cancelTitle = 'Cancel Rent Payments Subscription';
                    this.cancelMessage = 'Are you sure you want to cancel your Rent Payments subscription? You will lose access to rent payment features.';
                    break;
                case 'maintenance':
                    this.cancelTitle = 'Cancel Maintenance Subscription';
                    this.cancelMessage = 'Are you sure you want to cancel your Maintenance subscription? You will lose access to maintenance request features.';
                    break;
                case 'protect':
                    this.cancelTitle = 'Cancel RiskGuard Subscription';
                    this.cancelMessage = 'Are you sure you want to cancel your RiskGuard subscription? This will also cancel your Rent Payments and Maintenance subscriptions.';
                    break;
                case 'protect-pro':
                    this.cancelTitle = 'Cancel RiskGuard Pro Subscription';
                    this.cancelMessage = "Are you sure you want to cancel your RiskGuard Pro subscription? You'll keep Pro features until the end of your paid year, then move to Basic. Your properties are kept.";
                    break;
            }

            this.showCancelModal = true;
        },
        // RAC-10387: cancel a single property's feature subscription (immediate, not period-end)
        showPropertyCancelConfirmation(feature, prop) {
            const label = feature === 'rent_payment' ? 'Rent Payments' : 'Maintenance';
            const access = feature === 'rent_payment' ? 'rent payment' : 'maintenance request';
            this.cancelAction = `${feature}_property`;
            this.cancelPropertyId = prop.id;
            this.cancelTitle = `Cancel ${label} for this Property`;
            this.cancelMessage = `Are you sure you want to cancel ${label} for ${prop.full_address}? This takes effect immediately — tenants on this property will immediately lose ${access} access. Any prorated credit for unused time may be applied to your account.`;
            this.cancelConfirmText = 'Yes, Remove Property';
            this.showCancelModal = true;
        },
        closeCancelModal() {
            this.showCancelModal = false;
            this.cancelAction = null;
            this.cancelTitle = '';
            this.cancelMessage = '';
            this.cancelConfirmText = 'Yes, Cancel Subscription';
            this.cancelPropertyId = null;
        },
        confirmCancel() {
            if (!this.cancelAction) return;

            switch (this.cancelAction) {
                case 'rent_payment':
                    this.cancelRentPaymentSubscription();
                    break;
                case 'maintenance':
                    this.cancelMaintenanceSubscription();
                    break;
                case 'rent_payment_property':
                    this.cancelPropertyFeatureSubscription(route('rent-payments.property-subscription.cancel'));
                    break;
                case 'maintenance_property':
                    this.cancelPropertyFeatureSubscription(route('maintenance.property-subscription.cancel'));
                    break;
                case 'protect':
                case 'protect-pro':
                    this.cancelProtectSubscription();
                    break;
            }

            this.closeCancelModal();
        },
        async createLinkToken(product) {
            this.isLoader = true;
            try {
                const response = await axios.post(route('plaid.create-link-token'), { product });
                if (response.data.status) {
                    this.initializePlaidLink(response.data.public_token, product);
                }
            } catch (error) {
                this.isLoader = false;
                console.error('Error creating link token.');
            }
        },
        async initializePlaidLink(public_token, product) {
            var handler = Plaid.create({
                token: public_token,
                onLoad: function () {},
                onSuccess: (public_token, metadata) => {
                    this.isLoader = true;
                    if (product === 'auth') {
                        this.finishAuth(public_token, metadata);
                    } else if (product === 'identity_verification') {
                        this.finishIdv(public_token, metadata);
                    }
                },
                onExit: (err) => {
                    this.isLoader = false;
                    if (err != null) {
                        console.error('Plaid error.');
                    }
                },
                onEvent: function () {},
            });

            handler.open();
        },
        async finishAuth(public_token, metadata) {
            try {
                await axios.post(route('plaid.exchange-token-and-retrieve-account'), { public_token, metadata });
                this.isLoader = false;
                location.reload();
            } catch (error) {
                this.isLoader = false;
            }
        },
        async finishIdv(public_token, metadata) {
            try {
                await axios.post(route('plaid.idv.submit'), { public_token, metadata });
                this.isLoader = false;
                location.reload();
            } catch (error) {
                this.isLoader = false;
            }
        },
        onboardingStep1() {
            this.isShowStep1Modal = true;
        },
        proceedToOnboarding() {
            this.isShowStep1Modal = false;
            this.$inertia.visit(route('plaid.user-onboarding'), {
                method: 'get',
                data: { business_type: this.business_type },
            });
        },
        async savePhoneNumber() {
            if (!this.phoneForm.phone || this.phoneForm.phone.length !== 10) {
                this.phoneForm.errors.phone = 'Phone number must be exactly 10 digits';
                return;
            }

            this.isSubmitting = true;
            try {
                await this.$inertia.post(route('users.save-phone'), {
                    phone: this.phoneForm.phone,
                    redirect_after_phone_added: 'payment-methods.index',
                }, {
                    onSuccess: () => {
                        this.showPhoneModal = false;
                        this.phoneForm.phone = '';
                        this.phoneForm.errors = {};
                        this.$inertia.reload();
                    },
                    onError: (errors) => {
                        this.phoneForm.errors = errors;
                    },
                    onFinish: () => {
                        this.isSubmitting = false;
                    },
                });
            } catch (error) {
                this.isSubmitting = false;
                console.error('Error saving phone number.');
            }
        },
        closePhoneModal() {
            this.showPhoneModal = false;
            this.phoneForm.phone = '';
            this.phoneForm.errors = {};
        },
    },
};
</script>
